import { Button, useToast } from '@quadminds/mindkit';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActiveConnection from './Active/ActiveConnection';
import ThereIsNotActives from './Active/ThereIsNotActives';
import AvailableConection from './Availables/AvailableConnection';
import FrameSection from './FrameSection';
import { useAuthYpf } from '../../../../hooks/useAuthYpf';
import { useDeleteGoogleAuth } from '../../../../hooks/useDeleteGoogleAuth';
import { useDeleteYpfAuth } from '../../../../hooks/useDeleteYpfAuth';
import { useGetApplications } from '../../../../hooks/useGetApplications';
import { AuthorizedProvider, useGetAuthorizedProviders } from '../../../../hooks/useGetAutorizedProviders';
import { useGetGoogleAuthCode } from '../../../../hooks/useGetGoogleCode';
import { Provider, useGetProviders } from '../../../../hooks/useGetProviders';
import { YpfImportResult } from '../../../../interfaces/ypf';
import { useIntegrationContext } from '../../../../providers/Integration/Integration.provider';
import SuspenseCustom from '../../../common/SuspenseCustom/SuspenseCustom';
import FrameLayout from '../FrameLayout/FrameLayout';
import YpfIntegrationResult from '../YpfIntegrationModals/YpfIntegrationResult';
import YpfIntegrationUpload from '../YpfIntegrationModals/YpfIntegrationUpload';

const PROVIDERS_ENABLED_FIXED = ['google_drive', 'ypf_lp4'];

export type YpfImportResponse =
    | { ok: true; data: YpfImportResult }
    | { ok: false; error: { details?: { code: string } } };

const Connections = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [showYpfUploadModal, setShowYpfUploadModal] = useState(false);
    const [showYpfResultModal, setShowYpfResultModal] = useState(false);
    const [results, setResults] = useState<YpfImportResponse | null>(null);
    const [file, setFile] = useState<File | null>(null);

    const { data: apps, isLoading: isLoadingApps, isError: isErrorApps } = useGetApplications();
    const { mutateAsync: getGoogleAuthCode, isPending: isLoadingRedirect } = useGetGoogleAuthCode();
    const { mutateAsync: deleteGoogleAuth, isPending: isDeleteGoogleAuth } = useDeleteGoogleAuth();
    const { mutateAsync: autorizeYpf, isPending: isLoadingAuthoriceYpf } = useAuthYpf();
    const { mutateAsync: deleteYpfAuth, isPending: isLoadingDeleteYpfAuth } = useDeleteYpfAuth();
    const { setGoogleIntegration } = useIntegrationContext();

    const { toast } = useToast();

    const mindyApp = useMemo(() => apps?.find((app) => app.name === 'mindy'), [apps]);

    const { data: providers, isLoading: isLoadingProviders, isError: isErrorProviders } = useGetProviders(mindyApp?.id);

    const {
        data: authorizations,
        isLoading: isLoadingAuthorizations,
        isFetching: isFetchingAuthorizations,
        isError: isErrorAuthorizations,
    } = useGetAuthorizedProviders();

    const mindyFixedProviders = (providers ?? [])
        .filter((provider: Provider) => PROVIDERS_ENABLED_FIXED.includes(provider.name))
        .reduce((total: Record<string, Provider>, current: Provider) => {
            return { ...total, [current.name]: current };
        }, {});

    const activeProviders: Record<string, AuthorizedProvider> = useMemo(() => {
        if (authorizations === undefined) return {};
        return authorizations
            .filter((auth) => auth.authorization.completed)
            .reduce((total: Record<string, AuthorizedProvider>, current: AuthorizedProvider) => {
                return { ...total, [current.name]: current };
            }, {});
    }, [authorizations]);

    const isGoogleConnected = useMemo(() => Object.keys(activeProviders).includes('google_drive'), [activeProviders]);
    const isYpfConnected = useMemo(() => Object.keys(activeProviders).includes('ypf_lp4'), [activeProviders]);

    const handleDeleteGoogle = useCallback(() => {
        deleteGoogleAuth()
            .then(() => {
                setGoogleIntegration({ accessToken: undefined, selfOpenedGooglePicker: false });
                queryClient.invalidateQueries({ queryKey: ['authorizedProviders'] });
            })
            .catch(() => {
                toast({
                    title: t('settings.connections.error.googleConnectionErrorTitle'),
                    description: t('settings.connections.error.googleConnectionErrorDescription'),
                    variant: 'destructive',
                });
            });
    }, [deleteGoogleAuth, queryClient, setGoogleIntegration, t, toast]);

    const handleConnectGoogle = useCallback(() => {
        if (isGoogleConnected) {
            // desconectar
            handleDeleteGoogle();
        } else {
            localStorage.setItem('googleOriginPath', '/settings/connections');
            // conectar
            getGoogleAuthCode(window.location.origin + '/apps/mindy-app/auth')
                .then((response) => {
                    window.location.href = response.data.authorization_url; // Redirect to the Google Auth URL
                })
                .catch((error) => {
                    console.error('Error getting Google Auth Code:', error);
                });
        }
    }, [isGoogleConnected, getGoogleAuthCode, handleDeleteGoogle]);

    const handleDeleteYpf = useCallback(() => {
        deleteYpfAuth()
            .then(() => {
                queryClient.invalidateQueries({ queryKey: ['authorizedProviders'] });
            })
            .catch(() => {
                toast({
                    title: t('settings.connections.error.ypfDisconnectionErrorTitle'),
                    description: t('settings.connections.error.ypfDisconnectionErrorDescription'),
                    variant: 'destructive',
                });
            });
    }, [deleteYpfAuth, queryClient, t, toast]);

    const handleConnectYpf = useCallback(() => {
        if (isYpfConnected) {
            // desconectar
            handleDeleteYpf();
        } else {
            // conectar
            autorizeYpf()
                .then(() => {
                    queryClient.invalidateQueries({ queryKey: ['authorizedProviders'] });
                })
                .catch(() => {
                    toast({
                        title: t('settings.connections.error.ypfConnectionErrorTitle'),
                        description: t('settings.connections.error.ypfConnectionErrorDescription'),
                        variant: 'destructive',
                    });
                });
        }
    }, [isYpfConnected, autorizeYpf, handleDeleteYpf, queryClient, t, toast]);

    return (
        <FrameLayout title={t('settings.connections.title')}>
            <FrameSection title={t('settings.connections.subtitleActive')}>
                <SuspenseCustom
                    isLoading={isLoadingAuthorizations || isFetchingAuthorizations || isLoadingDeleteYpfAuth}
                    isError={isErrorAuthorizations || Object.keys(activeProviders).length === 0}
                    errorFallback={
                        <ThereIsNotActives
                            title={t('settings.connections.error.active.empty.title')}
                            subtitle={t('settings.connections.error.active.empty.subtitle')}
                        />
                    }
                    className="h-[70px]"
                >
                    {isGoogleConnected && (
                        <ActiveConnection
                            key={activeProviders['google_drive']._id}
                            name={t(
                                `settings.connections.providers.active.${activeProviders['google_drive'].name}.name`,
                            )}
                            description={t(
                                `settings.connections.providers.active.${activeProviders['google_drive'].name}.subtitle`,
                            )}
                            image={mindyFixedProviders[activeProviders['google_drive'].name]?.icon || ''}
                            id={activeProviders['google_drive']._id}
                        >
                            <Button variant="secondary" onClick={handleDeleteGoogle}>
                                {t('settings.connections.deactivate')}
                            </Button>
                        </ActiveConnection>
                    )}
                    {isYpfConnected && (
                        <ActiveConnection
                            key={activeProviders['ypf_lp4']._id}
                            name={t(`settings.connections.providers.active.${activeProviders['ypf_lp4'].name}.name`)}
                            description={t(
                                `settings.connections.providers.active.${activeProviders['ypf_lp4'].name}.subtitle`,
                            )}
                            image={mindyFixedProviders[activeProviders['ypf_lp4'].name]?.icon || ''}
                            id={activeProviders['ypf_lp4']._id}
                            className="mt-4"
                        >
                            <Button onClick={() => setShowYpfUploadModal(true)}>
                                {t('settings.connections.uploadFile')}
                            </Button>
                            <Button variant="secondary" onClick={() => handleDeleteYpf()}>
                                {t('settings.connections.deactivate')}
                            </Button>
                        </ActiveConnection>
                    )}
                </SuspenseCustom>
            </FrameSection>
            <FrameSection title={t('settings.connections.subtitleAvailable')}>
                <SuspenseCustom
                    isLoading={isLoadingProviders || isLoadingApps}
                    isError={isErrorProviders || isErrorApps}
                    errorFallback={
                        <ThereIsNotActives
                            title={t('settings.connections.error.availables.empty.title')}
                            subtitle={t('settings.connections.error.availables.empty.title')}
                        />
                    }
                    className="h-80"
                >
                    <div className="flex flex-row gap-4">
                        {Object.keys(mindyFixedProviders).includes('google_drive') && (
                            <AvailableConection
                                key={mindyFixedProviders['google_drive'].id}
                                id={mindyFixedProviders['google_drive'].id}
                                title={t(
                                    `settings.connections.providers.availables.${mindyFixedProviders['google_drive'].name}.name`,
                                )}
                                subtitle={t(
                                    `settings.connections.providers.availables.${mindyFixedProviders['google_drive'].name}.subtitle`,
                                )}
                                description={t(
                                    `settings.connections.providers.availables.${mindyFixedProviders['google_drive'].name}.description`,
                                )}
                                onConnect={handleConnectGoogle}
                                image={mindyFixedProviders['google_drive'].icon}
                                isConnected={isGoogleConnected}
                                isLoading={isLoadingRedirect || isDeleteGoogleAuth}
                            />
                        )}
                        {Object.keys(mindyFixedProviders).includes('ypf_lp4') && (
                            <AvailableConection
                                key={mindyFixedProviders['ypf_lp4'].id}
                                id={mindyFixedProviders['ypf_lp4'].id}
                                title={t(
                                    `settings.connections.providers.availables.${mindyFixedProviders['ypf_lp4'].name}.name`,
                                )}
                                subtitle={t(
                                    `settings.connections.providers.availables.${mindyFixedProviders['ypf_lp4'].name}.subtitle`,
                                )}
                                description={t(
                                    `settings.connections.providers.availables.${mindyFixedProviders['ypf_lp4'].name}.description`,
                                )}
                                onConnect={handleConnectYpf}
                                image={mindyFixedProviders['ypf_lp4'].icon}
                                isConnected={isYpfConnected}
                                isLoading={isLoadingAuthoriceYpf}
                            />
                        )}
                    </div>
                </SuspenseCustom>
            </FrameSection>
            <YpfIntegrationUpload
                isOpen={showYpfUploadModal}
                handleCloseModal={(value) => setShowYpfUploadModal(value)}
                onSuccess={(results: YpfImportResponse, file: File) => {
                    setResults(results);
                    setFile(file);
                    setShowYpfUploadModal(false);
                    setShowYpfResultModal(true);
                }}
            />
            <YpfIntegrationResult
                file={file}
                results={results}
                isOpen={showYpfResultModal}
                handleCloseModal={(value) => setShowYpfResultModal(value)}
            />
        </FrameLayout>
    );
};

export default Connections;
